import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Phone, ArrowRight, Leaf, CheckCircle2, Send } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialState = {
  // Personal
  firstName: '', lastName: '', age: '', height: '', dateOfBirth: '', placeOfBirth: '',
  email: '', emailFrequency: '',
  homePhone: '', workPhone: '', mobilePhone: '',
  currentWeight: '', weightSixMonthsAgo: '', weightOneYearAgo: '',
  weightDifferent: '', weightHow: '',

  // Social
  relationshipStatus: '', whereLive: '', children: '', pets: '',
  occupation: '', hoursWork: '',

  // General Health
  mainConcerns: '', otherConcernsGoals: '', bestPointInLife: '',
  illnessesInjuries: '', motherHealth: '', fatherHealth: '',
  ancestry: '', bloodType: '',

  // Sleep & body
  sleepQuality: '', hoursSleep: '', wakeAtNight: '',
  painStiffness: '', digestiveIssues: '', allergiesSensitivities: '',

  // Women's Health
  periodsRegular: '', flowDays: '', flowFrequency: '',
  periodsSymptomatic: '', menopause: '', birthControlHistory: '',
  yeastUti: '',

  // Medical
  supplementsMedications: '', otherTherapies: '', sportsExercise: '',

  // Food
  familySupport: '', doYouCook: '', percentHomeCooked: '',
  nonHomeCookedFrom: '',
  childBreakfast: '', childLunch: '', childDinner: '', childSnacks: '', childLiquids: '',
  todayBreakfast: '', todayLunch: '', todayDinner: '', todaySnacks: '', todayLiquids: '',
  cravings: '', dietImportantChange: '',

  // Additional
  additionalComments: '',
  consent: false,
};

const HealthCoaching = () => {
  const [data, setData] = useState(initialState);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const navigate = useNavigate();

  const upd = (k, v) => setData((p) => ({ ...p, [k]: v }));

  const scrollToForm = () => {
    const el = document.getElementById('intake-form');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!data.firstName || !data.lastName || !data.email) {
      toast.error('Please share your name and email');
      return;
    }
    if (!data.mobilePhone && !data.homePhone && !data.workPhone) {
      toast.error('Please share at least one phone number');
      return;
    }
    if (!data.consent) {
      toast.error('Please confirm to submit');
      return;
    }
    try {
      setSubmitting(true);
      await axios.post(`${API}/health-coaching/inquiries`, data);
      setDone(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Something went wrong. Please try again.');
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="hc-page" data-testid="hc-page">
        <header className="header-nav">
          <div className="header-content">
            <Link to="/" data-testid="hc-logo">
              <img src="/brand/logo.png" alt="Momshiach" className="logo-img" />
            </Link>
          </div>
        </header>
        <div className="hc-success" data-testid="hc-success">
          <div className="payment-result-icon success">
            <CheckCircle2 size={48} />
          </div>
          <h1 className="heading-1">Thank you 🌿</h1>
          <p className="body-large">
            Your intake is safely with me. I read every response personally. I'll reach out via <strong>WhatsApp</strong> within a few days so we can find the shape of support that fits your life right now.
          </p>
          <p className="body-medium">In the meantime, remember: <em>we don't remove — we add color.</em></p>
          <button onClick={() => navigate('/')} className="btn-primary btn-lg" data-testid="hc-return-home">
            Return Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="hc-page" data-testid="hc-page">
      {/* Header */}
      <header className="header-nav">
        <div className="header-content">
          <Link to="/" data-testid="hc-logo">
            <img src="/brand/logo.png" alt="Momshiach" className="logo-img" />
          </Link>
          <nav className="desktop-nav">
            <Link to="/" className="nav-link" data-testid="hc-nav-home">Home</Link>
            <Link to="/carebnb" className="nav-link" data-testid="hc-nav-carebnb">CareBnB</Link>
            <Link to="/resources" className="nav-link" data-testid="hc-nav-resources">Resources</Link>
          </nav>
          <div className="nav-actions">
            <a href="https://wa.me/972529326458" target="_blank" rel="noopener noreferrer" className="btn-secondary btn-contact" data-testid="hc-header-contact">
              <Phone size={16} />
              <span>Contact</span>
            </a>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="hc-hero">
        <div className="container hc-hero-grid">
          <div>
            <div className="hc-eyebrow"><Leaf size={14} /><span>Health Coaching · 1:1</span></div>
            <h1 className="heading-hero" data-testid="hc-hero-title">
              Add color<br />to your life.<br />Don't remove anything from it.
            </h1>
            <p className="body-large hc-hero-sub">
              A gentle, judgment-free coaching arc for women who are curious how food and lifestyle can nourish them — <em>as they already are</em>. No agenda, no diet, no rules.
            </p>
            <div className="hero-cta-group">
              <button onClick={scrollToForm} className="btn-primary btn-lg" data-testid="hc-start-intake-btn">
                Start Your Intake <ArrowRight size={18} />
              </button>
              <a href="https://wa.me/972529326458" target="_blank" rel="noopener noreferrer" className="btn-secondary btn-lg">
                Ask a Question First
              </a>
            </div>
          </div>
          <div className="hc-hero-image">
            <img src="/services/health-coaching-hero.png" alt="Health Coaching" />
          </div>
        </div>
      </section>

      {/* Philosophy strip */}
      <section className="hc-philosophy">
        <div className="container">
          <div className="hc-phil-grid">
            <div><strong>No judgment.</strong> I don't tell you what to remove.</div>
            <div><strong>No agenda.</strong> I meet your plate where it already is.</div>
            <div><strong>Just color.</strong> We add — colorful foods, gentle habits, real support.</div>
          </div>
        </div>
      </section>

      {/* Video Testimonials */}
      <section className="hc-testimonials-section" data-testid="hc-testimonials">
        <div className="container">
          <div className="section-header">
            <h2 className="heading-1">Real women. Real stories.</h2>
            <p className="body-large text-secondary">Hear from moms who found their color again.</p>
          </div>
          <div className="hc-video-grid">
            <div className="hc-video-card">
              <div className="video-embed">
                <iframe
                  src="https://www.youtube.com/embed/MBhuvoONY4A"
                  title="Health Coaching Testimonial 1"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  data-testid="hc-testimonial-video-1"
                />
              </div>
            </div>
            <div className="hc-video-card">
              <div className="video-embed">
                <iframe
                  src="https://www.youtube.com/embed/ql-ADypPEpY"
                  title="Health Coaching Testimonial 2"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  data-testid="hc-testimonial-video-2"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Intake Form */}
      <section id="intake-form" className="hc-form-section">
        <div className="container container-narrow">
          <div className="section-header">
            <h2 className="heading-1">Your Intake Form</h2>
            <p className="body-large text-secondary">Take your time. Write as much or as little as feels right — I read every word.</p>
          </div>

          <form onSubmit={submit} className="hc-form" data-testid="hc-intake-form">
            {/* PERSONAL */}
            <fieldset className="hc-fieldset">
              <legend className="hc-legend">Personal</legend>
              <div className="form-row">
                <div className="form-group"><label className="form-label">First Name *</label><input className="form-input" value={data.firstName} onChange={(e) => upd('firstName', e.target.value)} data-testid="hc-first-name" /></div>
                <div className="form-group"><label className="form-label">Last Name *</label><input className="form-input" value={data.lastName} onChange={(e) => upd('lastName', e.target.value)} data-testid="hc-last-name" /></div>
              </div>
              <div className="form-row-4">
                <div className="form-group"><label className="form-label">Age</label><input className="form-input" type="number" value={data.age} onChange={(e) => upd('age', e.target.value)} /></div>
                <div className="form-group"><label className="form-label">Height</label><input className="form-input" value={data.height} onChange={(e) => upd('height', e.target.value)} placeholder="5'4&quot;" /></div>
                <div className="form-group"><label className="form-label">Date of Birth</label><input className="form-input" type="date" value={data.dateOfBirth} onChange={(e) => upd('dateOfBirth', e.target.value)} /></div>
                <div className="form-group"><label className="form-label">Place of Birth</label><input className="form-input" value={data.placeOfBirth} onChange={(e) => upd('placeOfBirth', e.target.value)} /></div>
              </div>
              <div className="form-row">
                <div className="form-group"><label className="form-label">Email *</label><input className="form-input" type="email" value={data.email} onChange={(e) => upd('email', e.target.value)} data-testid="hc-email" /></div>
                <div className="form-group"><label className="form-label">How often do you check email?</label><input className="form-input" value={data.emailFrequency} onChange={(e) => upd('emailFrequency', e.target.value)} /></div>
              </div>
              <div className="form-row-3">
                <div className="form-group"><label className="form-label">Home Phone</label><input className="form-input" value={data.homePhone} onChange={(e) => upd('homePhone', e.target.value)} /></div>
                <div className="form-group"><label className="form-label">Work Phone</label><input className="form-input" value={data.workPhone} onChange={(e) => upd('workPhone', e.target.value)} /></div>
                <div className="form-group"><label className="form-label">Mobile Phone *</label><input className="form-input" value={data.mobilePhone} onChange={(e) => upd('mobilePhone', e.target.value)} data-testid="hc-mobile" /></div>
              </div>
              <div className="form-row-3">
                <div className="form-group"><label className="form-label">Current Weight</label><input className="form-input" value={data.currentWeight} onChange={(e) => upd('currentWeight', e.target.value)} /></div>
                <div className="form-group"><label className="form-label">Weight 6 Months Ago</label><input className="form-input" value={data.weightSixMonthsAgo} onChange={(e) => upd('weightSixMonthsAgo', e.target.value)} /></div>
                <div className="form-group"><label className="form-label">Weight 1 Year Ago</label><input className="form-input" value={data.weightOneYearAgo} onChange={(e) => upd('weightOneYearAgo', e.target.value)} /></div>
              </div>
              <div className="form-row">
                <div className="form-group"><label className="form-label">Would you like your weight to be different?</label><input className="form-input" value={data.weightDifferent} onChange={(e) => upd('weightDifferent', e.target.value)} /></div>
                <div className="form-group"><label className="form-label">If so, how?</label><input className="form-input" value={data.weightHow} onChange={(e) => upd('weightHow', e.target.value)} /></div>
              </div>
            </fieldset>

            {/* SOCIAL */}
            <fieldset className="hc-fieldset">
              <legend className="hc-legend">Social</legend>
              <div className="form-group"><label className="form-label">Relationship Status</label><input className="form-input" value={data.relationshipStatus} onChange={(e) => upd('relationshipStatus', e.target.value)} /></div>
              <div className="form-group"><label className="form-label">Where do you live?</label><input className="form-input" value={data.whereLive} onChange={(e) => upd('whereLive', e.target.value)} /></div>
              <div className="form-row">
                <div className="form-group"><label className="form-label">Any children?</label><input className="form-input" value={data.children} onChange={(e) => upd('children', e.target.value)} /></div>
                <div className="form-group"><label className="form-label">Any pets?</label><input className="form-input" value={data.pets} onChange={(e) => upd('pets', e.target.value)} /></div>
              </div>
              <div className="form-row">
                <div className="form-group"><label className="form-label">Occupation</label><input className="form-input" value={data.occupation} onChange={(e) => upd('occupation', e.target.value)} /></div>
                <div className="form-group"><label className="form-label">Hours worked per week</label><input className="form-input" type="number" value={data.hoursWork} onChange={(e) => upd('hoursWork', e.target.value)} /></div>
              </div>
            </fieldset>

            {/* GENERAL HEALTH */}
            <fieldset className="hc-fieldset">
              <legend className="hc-legend">General Health</legend>
              <div className="form-group"><label className="form-label">Main health concerns</label><textarea className="form-textarea" rows="3" value={data.mainConcerns} onChange={(e) => upd('mainConcerns', e.target.value)} data-testid="hc-main-concerns" /></div>
              <div className="form-group"><label className="form-label">Other concerns and/or goals</label><textarea className="form-textarea" rows="3" value={data.otherConcernsGoals} onChange={(e) => upd('otherConcernsGoals', e.target.value)} /></div>
              <div className="form-group"><label className="form-label">At what point in your life did you feel your best?</label><textarea className="form-textarea" rows="2" value={data.bestPointInLife} onChange={(e) => upd('bestPointInLife', e.target.value)} /></div>
              <div className="form-group"><label className="form-label">Current/previous serious illnesses, hospitalizations, or injuries</label><textarea className="form-textarea" rows="2" value={data.illnessesInjuries} onChange={(e) => upd('illnessesInjuries', e.target.value)} /></div>
              <div className="form-row">
                <div className="form-group"><label className="form-label">Mother's health</label><input className="form-input" value={data.motherHealth} onChange={(e) => upd('motherHealth', e.target.value)} /></div>
                <div className="form-group"><label className="form-label">Father's health</label><input className="form-input" value={data.fatherHealth} onChange={(e) => upd('fatherHealth', e.target.value)} /></div>
              </div>
              <div className="form-row">
                <div className="form-group"><label className="form-label">Ancestry</label><input className="form-input" value={data.ancestry} onChange={(e) => upd('ancestry', e.target.value)} /></div>
                <div className="form-group"><label className="form-label">Blood type</label><input className="form-input" value={data.bloodType} onChange={(e) => upd('bloodType', e.target.value)} /></div>
              </div>
              <div className="form-row">
                <div className="form-group"><label className="form-label">How is your sleep?</label><input className="form-input" value={data.sleepQuality} onChange={(e) => upd('sleepQuality', e.target.value)} /></div>
                <div className="form-group"><label className="form-label">Hours of sleep per night</label><input className="form-input" type="number" value={data.hoursSleep} onChange={(e) => upd('hoursSleep', e.target.value)} /></div>
              </div>
              <div className="form-group"><label className="form-label">Do you wake up during the night? If so, why?</label><textarea className="form-textarea" rows="2" value={data.wakeAtNight} onChange={(e) => upd('wakeAtNight', e.target.value)} /></div>
              <div className="form-group"><label className="form-label">Any pain, stiffness, or swelling?</label><textarea className="form-textarea" rows="2" value={data.painStiffness} onChange={(e) => upd('painStiffness', e.target.value)} /></div>
              <div className="form-group"><label className="form-label">Any constipation, diarrhea, or gas?</label><textarea className="form-textarea" rows="2" value={data.digestiveIssues} onChange={(e) => upd('digestiveIssues', e.target.value)} /></div>
              <div className="form-group"><label className="form-label">Any allergies or sensitivities?</label><textarea className="form-textarea" rows="2" value={data.allergiesSensitivities} onChange={(e) => upd('allergiesSensitivities', e.target.value)} data-testid="hc-allergies" /></div>
            </fieldset>

            {/* WOMEN'S HEALTH */}
            <fieldset className="hc-fieldset">
              <legend className="hc-legend">Women's Health</legend>
              <div className="form-row-3">
                <div className="form-group"><label className="form-label">Periods regular?</label><input className="form-input" value={data.periodsRegular} onChange={(e) => upd('periodsRegular', e.target.value)} /></div>
                <div className="form-group"><label className="form-label">Days of flow</label><input className="form-input" value={data.flowDays} onChange={(e) => upd('flowDays', e.target.value)} /></div>
                <div className="form-group"><label className="form-label">Frequency</label><input className="form-input" value={data.flowFrequency} onChange={(e) => upd('flowFrequency', e.target.value)} /></div>
              </div>
              <div className="form-group"><label className="form-label">Are your periods painful or symptomatic? Please explain.</label><textarea className="form-textarea" rows="2" value={data.periodsSymptomatic} onChange={(e) => upd('periodsSymptomatic', e.target.value)} /></div>
              <div className="form-group"><label className="form-label">Have you reached or are you approaching menopause? Please explain.</label><textarea className="form-textarea" rows="2" value={data.menopause} onChange={(e) => upd('menopause', e.target.value)} /></div>
              <div className="form-group"><label className="form-label">Birth control history</label><textarea className="form-textarea" rows="2" value={data.birthControlHistory} onChange={(e) => upd('birthControlHistory', e.target.value)} /></div>
              <div className="form-group"><label className="form-label">Do you experience yeast infections or UTIs? Please explain.</label><textarea className="form-textarea" rows="2" value={data.yeastUti} onChange={(e) => upd('yeastUti', e.target.value)} /></div>
            </fieldset>

            {/* MEDICAL */}
            <fieldset className="hc-fieldset">
              <legend className="hc-legend">Medical</legend>
              <div className="form-group"><label className="form-label">All supplements or medications</label><textarea className="form-textarea" rows="3" value={data.supplementsMedications} onChange={(e) => upd('supplementsMedications', e.target.value)} /></div>
              <div className="form-group"><label className="form-label">Any other healers, helpers, or therapies you're involved with?</label><textarea className="form-textarea" rows="2" value={data.otherTherapies} onChange={(e) => upd('otherTherapies', e.target.value)} /></div>
              <div className="form-group"><label className="form-label">What role do sports and exercise play in your life?</label><textarea className="form-textarea" rows="2" value={data.sportsExercise} onChange={(e) => upd('sportsExercise', e.target.value)} /></div>
            </fieldset>

            {/* FOOD */}
            <fieldset className="hc-fieldset">
              <legend className="hc-legend">Food</legend>
              <div className="form-group"><label className="form-label">Will family/friends be supportive of your desire to make food or lifestyle changes?</label><textarea className="form-textarea" rows="2" value={data.familySupport} onChange={(e) => upd('familySupport', e.target.value)} /></div>
              <div className="form-row">
                <div className="form-group"><label className="form-label">Do you cook?</label><input className="form-input" value={data.doYouCook} onChange={(e) => upd('doYouCook', e.target.value)} /></div>
                <div className="form-group"><label className="form-label">% of food home-cooked</label><input className="form-input" value={data.percentHomeCooked} onChange={(e) => upd('percentHomeCooked', e.target.value)} /></div>
              </div>
              <div className="form-group"><label className="form-label">Where does non-home-cooked food come from?</label><input className="form-input" value={data.nonHomeCookedFrom} onChange={(e) => upd('nonHomeCookedFrom', e.target.value)} /></div>

              <div className="hc-food-block">
                <h4 className="heading-3">Foods you ate often as a child</h4>
                <div className="form-row-5">
                  <div className="form-group"><label className="form-label">Breakfast</label><textarea className="form-textarea" rows="2" value={data.childBreakfast} onChange={(e) => upd('childBreakfast', e.target.value)} /></div>
                  <div className="form-group"><label className="form-label">Lunch</label><textarea className="form-textarea" rows="2" value={data.childLunch} onChange={(e) => upd('childLunch', e.target.value)} /></div>
                  <div className="form-group"><label className="form-label">Dinner</label><textarea className="form-textarea" rows="2" value={data.childDinner} onChange={(e) => upd('childDinner', e.target.value)} /></div>
                  <div className="form-group"><label className="form-label">Snacks</label><textarea className="form-textarea" rows="2" value={data.childSnacks} onChange={(e) => upd('childSnacks', e.target.value)} /></div>
                  <div className="form-group"><label className="form-label">Liquids</label><textarea className="form-textarea" rows="2" value={data.childLiquids} onChange={(e) => upd('childLiquids', e.target.value)} /></div>
                </div>
              </div>

              <div className="hc-food-block">
                <h4 className="heading-3">Foods you typically eat these days</h4>
                <div className="form-row-5">
                  <div className="form-group"><label className="form-label">Breakfast</label><textarea className="form-textarea" rows="2" value={data.todayBreakfast} onChange={(e) => upd('todayBreakfast', e.target.value)} data-testid="hc-today-breakfast" /></div>
                  <div className="form-group"><label className="form-label">Lunch</label><textarea className="form-textarea" rows="2" value={data.todayLunch} onChange={(e) => upd('todayLunch', e.target.value)} /></div>
                  <div className="form-group"><label className="form-label">Dinner</label><textarea className="form-textarea" rows="2" value={data.todayDinner} onChange={(e) => upd('todayDinner', e.target.value)} /></div>
                  <div className="form-group"><label className="form-label">Snacks</label><textarea className="form-textarea" rows="2" value={data.todaySnacks} onChange={(e) => upd('todaySnacks', e.target.value)} /></div>
                  <div className="form-group"><label className="form-label">Liquids</label><textarea className="form-textarea" rows="2" value={data.todayLiquids} onChange={(e) => upd('todayLiquids', e.target.value)} /></div>
                </div>
              </div>

              <div className="form-group"><label className="form-label">Do you crave sugar, coffee, or cigarettes? Other major addictions?</label><textarea className="form-textarea" rows="2" value={data.cravings} onChange={(e) => upd('cravings', e.target.value)} /></div>
              <div className="form-group"><label className="form-label">Most important diet change to improve your health?</label><textarea className="form-textarea" rows="2" value={data.dietImportantChange} onChange={(e) => upd('dietImportantChange', e.target.value)} /></div>
            </fieldset>

            {/* ADDITIONAL */}
            <fieldset className="hc-fieldset">
              <legend className="hc-legend">Additional Comments</legend>
              <div className="form-group"><label className="form-label">Is there anything else you'd like to share?</label><textarea className="form-textarea" rows="4" value={data.additionalComments} onChange={(e) => upd('additionalComments', e.target.value)} data-testid="hc-additional" /></div>
            </fieldset>

            <div className="form-group">
              <label className="checkbox-label-block">
                <input type="checkbox" checked={data.consent} onChange={(e) => upd('consent', e.target.checked)} data-testid="hc-consent" />
                <span>I understand this is an inquiry — Chanah will follow up personally before any session is booked.</span>
              </label>
            </div>

            <div style={{ textAlign: 'center', marginTop: '2rem' }}>
              <button type="submit" className="btn-primary btn-lg" disabled={submitting || !data.consent} data-testid="hc-submit">
                <Send size={18} />
                <span>{submitting ? 'Sending…' : 'Send My Intake'}</span>
              </button>
            </div>
          </form>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-bottom">
            <p className="caption">© 2025 Momshiach · Health Coaching · Add color to your existing plate.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HealthCoaching;
